import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {
  IonContent,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonMenu,
  IonSplitPane,
  NavController,
} from '@ionic/angular/standalone';
import { filter } from 'rxjs';
import { SlidingTabsComponent } from '../sliding-tabs/sliding-tabs.component';
import { AppBrandLogoComponent } from '../app-brand-logo/app-brand-logo.component';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { DESKTOP_MIN_WIDTH_PX } from 'src/app/utils/responsive-nav-animation';
import {
  MAIN_TAB_DEFAULT_ID,
  MAIN_TABS,
  type MainTabId,
  mainTabFromUrl,
  mainTabLink,
} from './main-tabs.config';
import { MainTabsPreloadService } from './main-tabs-preload.service';

@Component({
  selector: 're-main-tabs',
  providers: [MainTabsPreloadService],
  imports: [
    IonContent,
    IonIcon,
    IonMenu,
    IonSplitPane,
    IonTabButton,
    IonTabBar,
    IonTabs,
    RouterLink,
    RouterLinkActive,
    AppBrandLogoComponent,
    SlidingTabsComponent,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './main-tabs.component.html',
  styleUrls: ['./main-tabs.component.scss'],
})
export class MainTabsComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly navController = inject(NavController);
  private readonly destroyRef = inject(DestroyRef);

  readonly isDesktop = signal(false);
  readonly tabs = MAIN_TABS;
  readonly brandLink = mainTabLink(MAIN_TAB_DEFAULT_ID);
  readonly tabLink = mainTabLink;
  readonly selectedTab = signal<MainTabId>(
    mainTabFromUrl(this.location.path() || this.router.url),
  );

  private mediaQuery?: MediaQueryList;
  private readonly onMediaChange = (event: MediaQueryListEvent) => {
    this.isDesktop.set(event.matches);
  };

  constructor() {
    const navSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.selectedTab.set(
          mainTabFromUrl(this.location.path() || event.urlAfterRedirects),
        );
      });
    this.destroyRef.onDestroy(() => navSub.unsubscribe());
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.mediaQuery = window.matchMedia(
      `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`,
    );
    this.isDesktop.set(this.mediaQuery.matches);
    this.mediaQuery.addEventListener('change', this.onMediaChange);
  }

  ngOnDestroy(): void {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
  }

  /** Tab switches replace the current hub; they must not push onto the back stack. */
  onMainTabClick(event: Event): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    const tab = (event as CustomEvent<{ tab?: string }>).detail?.tab;
    if (!tab || this.selectedTab() === tab) {
      return;
    }
    void this.navController.navigateRoot(mainTabLink(tab), {
      replaceUrl: true,
      animated: false,
    });
  }
}
